import React, { useState, useEffect } from "react";
import axios from "axios";
import pdfMake from "../pdfMakeSetup"; 
import { useSelector } from "react-redux";
import "./DailyClosure.css";

function DailyClosure() {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [passationClosure, setPassationClosure] = useState(null);
  const [endOfDayClosure, setEndOfDayClosure] = useState(null);
  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState("");
  const [closureAlreadyDone, setClosureAlreadyDone] = useState(false);
  const [allClosures, setAllClosures] = useState([]);
  // 🔥 AJOUT: États pour les tables en attente
  const [pendingTables, setPendingTables] = useState([]);
  const [hasPendingTables, setHasPendingTables] = useState(false);
  
  const authState = useSelector(state => state.AuthReducer);
  const user = authState?.user;

  // 🔥 EXTRACTION CORRECTE DE LA LOCALISATION
  const getUserLocationInfo = () => {
    if (!user) return { id: null, name: "Non connecté" };
    
    const loc = user.localisation;
    if (!loc) return { id: null, name: "Non spécifiée" };

    let locationId = null;
    let locationName = "Non spécifiée";

    // 🔥 PRIORITÉ AU FORMAT {$oid: ...}
    if (loc.$oid) {
      locationId = String(loc.$oid);
      locationName = user.name || `Localisation ${loc.$oid}`;
    } else if (loc._id) {
      locationId = String(loc._id);
      locationName = user.name || `Localisation ${loc._id}`;
    } else if (typeof loc === 'string') {
      locationId = loc;
      locationName = user.name || `Localisation ${loc}`;
    } else {
      locationId = String(loc);
      locationName = user.name || `Localisation ${locationId}`;
    }

    console.log("📍 Localisation utilisateur:", { locationId, locationName, userLocalisation: loc });
    return { id: locationId, name: locationName };
  };

  const { id: userLocationId, name: userLocationName } = getUserLocationInfo();

  // 🔥 FONCTION POUR EXTRACTER L'ID DE LOCALISATION D'UNE FACTURE OU CLÔTURE
  const getLocationId = (item) => {
    if (!item) return null;
    
    if (item.localisation) {
      if (item.localisation.$oid) {
        return String(item.localisation.$oid);
      } else if (item.localisation._id) {
        return String(item.localisation._id);
      } else if (typeof item.localisation === 'string') {
        return String(item.localisation);
      }
    }
    
    if (item.locationId) {
      return String(item.locationId);
    }
    
    return null;
  };

  // 🔥 RÉINITIALISER LES ÉTATS QUAND L'UTILISATEUR CHANGE
  useEffect(() => {
    console.log("🔄 CHANGEMENT UTILISATEUR - Réinitialisation des états");
    setPassationClosure(null);
    setEndOfDayClosure(null);
    setClosureAlreadyDone(false);
    setSales([]);
    setError("");
    setDebugInfo("");
    setAllClosures([]);
    // 🔥 AJOUT: Réinitialiser les tables en attente
    setPendingTables([]);
    setHasPendingTables(false);
  }, [user?._id, userLocationId]);

  // 🔥 AJOUT: FONCTION POUR VÉRIFIER LES TABLES EN ATTENTE
  // 🔥 AJOUT: FONCTION POUR VÉRIFIER LES TABLES EN ATTENTE AVEC FILTRAGE PAR LOCALISATION
const checkPendingTables = async () => {
  if (!userLocationId) return;
  
  try {
    console.log("🔄 Vérification des tables en attente pour:", userLocationId);
    
    // 🔥 MODIFICATION: Passer l'ID de localisation dans les paramètres de la requête
    const res = await axios.get("/api/PendingTables/getAllPendingTables", {
      params: {
        location: userLocationId
      }
    });
    
    const userPendingTables = res.data || [];
    
    setPendingTables(userPendingTables);
    setHasPendingTables(userPendingTables.length > 0);
    
    console.log(`📊 ${userPendingTables.length} tables en attente pour ${userLocationName}`, userPendingTables);
    
  } catch (error) {
    console.error("❌ Erreur vérification tables en attente:", error);
  }
};
  // 🔥 CHARGER LES VENTES DU JOUR POUR CETTE LOCALISATION UNIQUEMENT
  useEffect(() => {
    const fetchTodaySales = async () => {
      if (!user || !userLocationId) return;

      try {
        setLoading(true);
        setError("");
        
        console.log("🔄 Chargement des ventes pour localisation:", userLocationId);
        const res = await axios.get("/api/Bills/getAllBills");
        const allBills = res.data || [];
        
        const today = new Date();
        const todayKey = today.toISOString().split("T")[0];
        
        // 🔥 FILTRER PAR DATE ET LOCALISATION
        const todayBills = allBills.filter(bill => {
          if (!bill.date) return false;
          
          try {
            const billDate = new Date(bill.date);
            const billDateKey = billDate.toISOString().split("T")[0];
            const isToday = billDateKey === todayKey;
            
            if (!isToday) return false;

            // 🔥 VÉRIFICATION STRICTE DE LA LOCALISATION
            const billLocationId = getLocationId(bill);
            const isSameLocation = billLocationId && billLocationId === userLocationId;
            
            console.log(`📋 Facture ${bill._id}:`, {
              date: billDateKey,
              isToday,
              billLocationId,
              userLocationId,
              isSameLocation
            });

            return isSameLocation;
            
          } catch (dateError) {
            return false;
          }
        });

        console.log(`✅ ${todayBills.length} ventes trouvées pour ${userLocationName} aujourd'hui`);
        setSales(todayBills);

        if (todayBills.length === 0) {
          setError(`Aucune vente trouvée pour aujourd'hui dans votre localisation (${userLocationName})`);
        } else {
          setDebugInfo(`${todayBills.length} ventes trouvées pour aujourd'hui dans ${userLocationName}`);
        }

      } catch (err) {
        console.error("❌ Erreur chargement ventes:", err);
        setError("Erreur de chargement des ventes");
      } finally {
        setLoading(false);
      }
    };

    fetchTodaySales();
  }, [user, userLocationId, userLocationName]);

  // 🔥 CHARGER LES CLÔTURES POUR CETTE LOCALISATION UNIQUEMENT
  useEffect(() => {
    const fetchClosures = async () => {
      if (!userLocationId) return;

      try {
        console.log("🔄 Chargement des clôtures pour localisation:", userLocationId);
        const res = await axios.get("/api/CloseDay/getAllClosures");
        const closures = res.data || [];
        
        // 🔥 FILTRER PAR LOCALISATION
        const userClosures = closures.filter(closure => {
          const closureLocationId = getLocationId(closure);
          const isSameLocation = closureLocationId && closureLocationId === userLocationId;
          
          console.log(`📋 Clôture ${closure._id}:`, {
            closureLocationId,
            userLocationId,
            isSameLocation,
            type: closure.closureType
          });
          
          return isSameLocation;
        });

        setAllClosures(userClosures);
        console.log(`✅ ${userClosures.length} clôtures trouvées pour ${userLocationName}`);

        // Vérifier si une clôture de fin de journée existe déjà pour aujourd'hui
        const today = new Date().toISOString().split('T')[0];
        const todayClosures = userClosures.filter(closure => {
          const closureDate = new Date(closure.date).toISOString().split('T')[0];
          return closureDate === today;
        });

        const todayPassation = todayClosures.find(c => c.closureType === "passation");
        const todayEndOfDay = todayClosures.find(c => c.closureType === "fin_journee");

        if (todayPassation) {
          setPassationClosure(todayPassation);
          console.log("✅ Passation trouvée pour aujourd'hui");
        }
        if (todayEndOfDay) {
          setEndOfDayClosure(todayEndOfDay);
          setClosureAlreadyDone(true);
          console.log("✅ Fin de journée trouvée pour aujourd'hui - BLOQUÉE");
        }

        // 🔥 AJOUT: Vérifier les tables en attente
        await checkPendingTables();

      } catch (error) {
        console.error("❌ Erreur chargement clôtures:", error);
      }
    };

    fetchClosures();
  }, [userLocationId, userLocationName]);

  // 🔥 GÉNÉRATION PDF (CODE ORIGINAL SANS MODIFICATION)
  const generateDetailedPDF = (closureData, title, showClosureSeparation = false) => {
    if (!sales || sales.length === 0) {
      alert("Aucune donnée de vente disponible pour générer le PDF.");
      return;
    }

    const dateText = new Date().toLocaleDateString('fr-FR');
    const timeText = new Date().toLocaleTimeString('fr-FR');

    const tableData = [];
    let totalGeneral = 0;

    const sortedBills = [...sales].sort((a, b) => 
      new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt)
    );

    tableData.push([
      { text: "Heure", style: "tableHeader", bold: true },
      { text: "Table", style: "tableHeader", bold: true },
      { text: "Produit", style: "tableHeader", bold: true },
      { text: "Qté", style: "tableHeader", bold: true },
      { text: "Prix U.", style: "tableHeader", bold: true },
      { text: "Total", style: "tableHeader", bold: true }
    ]);

    let passationLineAdded = false;

    sortedBills.forEach(bill => {
      const billTime = new Date(bill.date || bill.createdAt).toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      
      const billTable = bill.tableNumber || "N/A";

      if (showClosureSeparation && passationClosure && !passationLineAdded) {
        const passationDate = new Date(passationClosure.date);
        const billDate = new Date(bill.date || bill.createdAt);
        
        if (billDate > passationDate) {
          tableData.push([
            { 
              text: `--- CLÔTURE DE PASSATION EFFECTUÉE À ${passationDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} ---`, 
              colSpan: 6, 
              style: "separationLine",
              bold: true,
              alignment: "center"
            },
            {}, {}, {}, {}, {}
          ]);
          passationLineAdded = true;
        }
      }

      if (bill.items && bill.items.length > 0) {
        bill.items.forEach((item, index) => {
          const itemTotal = (item.price || 0) * (item.quantity || 0);
          totalGeneral += itemTotal;

          tableData.push([
            index === 0 ? billTime : "",
            index === 0 ? billTable : "",
            item.name || "Produit non nommé",
            item.quantity || 0,
            `${(item.price || 0).toFixed(2)} DT`,
            `${itemTotal.toFixed(2)} DT`
          ]);
        });

        if (bill.items.length > 0) {
          tableData.push([
            { text: "", colSpan: 6, margin: [0, 2, 0, 2] }
          ]);
        }
      }
    });

    const content = [
      { text: title, style: "header" },
      { text: `Date : ${dateText}`, margin: [0, 5, 0, 2] },
      { text: `Heure : ${timeText}`, margin: [0, 0, 0, 2] },
      { text: `Localisation : ${userLocationName}`, margin: [0, 0, 0, 15] },
    ];

    content.push(
      { text: "DÉTAIL DES VENTES PAR TABLE", style: "subheader", margin: [0, 0, 0, 10] },
      {
        table: {
          headerRows: 1,
          widths: ['auto', 'auto', '*', 'auto', 'auto', 'auto'],
          body: tableData
        }
      },
      { 
        text: `TOTAL GÉNÉRAL: ${totalGeneral.toFixed(2)} DT`, 
        style: "total", 
        margin: [0, 15, 0, 5],
        alignment: "right"
      }
    );

    if (closureData && closureData.closureType === "fin_journee" && closureData.paymentStats) {
      const paymentStats = closureData.paymentStats || {};
      const paymentRows = Object.entries(paymentStats).map(
        ([method, amount]) => [method.replace(/_/g, " "), `${Number(amount || 0).toFixed(2)} DT`]
      );

      if (paymentRows.length > 0) {
        content.push(
          { text: "RÉPARTITION DES PAIEMENTS", style: "subheader", margin: [0, 20, 0, 10] },
          {
            table: {
              headerRows: 1,
              widths: ["*", "auto"],
              body: [
                ["Moyen de paiement", "Total (DT)"],
                ...paymentRows
              ],
            },
            margin: [0, 0, 0, 15],
          }
        );
      }
    }

    const docDefinition = {
      content: content,
      styles: {
        header: { fontSize: 16, bold: true, alignment: "center", margin: [0, 0, 0, 10] },
        subheader: { fontSize: 14, bold: true, margin: [0, 0, 0, 5] },
        tableHeader: { fontSize: 9, bold: true, fillColor: "#f5f5f5" },
        separationLine: { fontSize: 10, bold: true, color: "#e74c3c", margin: [0, 10, 0, 10], fillColor: "#fff3cd" },
        total: { fontSize: 14, bold: true, color: "#e74c3c" }
      },
      pageMargins: [20, 20, 20, 20],
      defaultStyle: { fontSize: 8 }
    };

    pdfMake.createPdf(docDefinition).open();
  };

  // 🔥 CLÔTURE PASSATION (CODE ORIGINAL)
  const handlePassationClosure = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (sales.length === 0) {
        alert("❌ Aucune vente à clôturer");
        return;
      }

      const paymentStats = {};
      sales.forEach(bill => {
        const method = bill.paymentMethod || "Non spécifié";
        paymentStats[method] = (paymentStats[method] || 0) + Number(bill.total || 0);
      });
      
      const passationData = {
        closureType: "passation",
        locationId: userLocationId,
        userId: user?._id,
        totalBills: sales.length,
        totalSales: sales.reduce((acc, bill) => acc + Number(bill.total || 0), 0),
        paymentStats: paymentStats,
        bills: sales.map(bill => bill._id)
      };

      console.log("📤 Création passation:", passationData);
      const res = await axios.post("/api/CloseDay/createClosure", passationData);
      
      setPassationClosure(res.data.closure);
      setAllClosures(prev => [res.data.closure, ...prev]);
      
      alert("✅ Clôture de passation créée avec succès.");
      generateDetailedPDF(res.data.closure, "RAPPORT DE PASSATION");

    } catch (err) {
      console.error("❌ Erreur passation:", err);
      alert("Erreur lors de la clôture de passation");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 CLÔTURE FIN DE JOURNÉE - AJOUT DE LA VÉRIFICATION DES TABLES EN ATTENTE
  const handleEndOfDayClosure = async () => {
    if (loading) return;
    
    if (closureAlreadyDone) {
      alert("🚫 CLÔTURE BLOQUÉE\n\nLa clôture de fin de journée a déjà été effectuée aujourd'hui.");
      return;
    }

    // 🔥 AJOUT: VÉRIFICATION DES TABLES EN ATTENTE
    if (hasPendingTables) {
      alert(`🚫 CLÔTURE IMPOSSIBLE\n\nIl y a ${pendingTables.length} table(s) en attente :\n\n${pendingTables.map(t => `• Table ${t.tableNumber} (${t.items?.length || 0} articles)`).join('\n')}\n\nVeuillez régulariser toutes les tables avant la clôture.`);
      return;
    }

    setLoading(true);
    
    try {
      console.log("🏁 Création clôture fin de journée pour:", userLocationName);

      if (!userLocationId || !user?._id) {
        alert("❌ Impossible de créer la clôture : utilisateur non identifié");
        return;
      }

      if (sales.length === 0) {
        alert("❌ Aucune vente à clôturer aujourd'hui");
        return;
      }

      // 🔥 ENVOYER UNIQUEMENT LA REQUÊTE - PAS DE DÉDUCTION DE STOCK
      const requestData = {
        closureType: "fin_journee",
        locationId: userLocationId,
        userId: user._id
      };

      console.log("📤 Création fin de journée:", requestData);
      const res = await axios.post("/api/CloseDay/createClosure", requestData);

      if (res.data && res.data.closure) {
        setEndOfDayClosure(res.data.closure);
        setClosureAlreadyDone(true);
        setAllClosures(prev => [res.data.closure, ...prev]);
        
        let alertMessage = `✅ CLÔTURE DE FIN DE JOURNÉE CRÉÉE AVEC SUCCÈS!\n\n`;
        alertMessage += `📍 Localisation: ${userLocationName}\n`;
        alertMessage += `📊 ${res.data.closure.totalBills} factures (${res.data.closure.totalSales?.toFixed(2)} DT)\n`;
        
        // 🔥 AFFICHER LES RÉSULTATS DE DÉDUCTION DU SERVEUR SI DISPONIBLES
        if (res.data.stockDeduction) {
          const stockResult = res.data.stockDeduction;
          alertMessage += `📦 ${stockResult.processedItems} items traités\n`;
          alertMessage += `🔄 ${stockResult.totalIngredientsUpdated} ingrédients mis à jour\n`;
          
          if (stockResult.errors && stockResult.errors.length > 0) {
            alertMessage += `\n⚠️ ${stockResult.errors.length} erreurs (voir logs serveur)`;
          }
        }
        
        alertMessage += `\n\n🚫 La clôture est maintenant BLOQUÉE.`;
        
        alert(alertMessage);
        
        setTimeout(() => {
          generateDetailedPDF(res.data.closure, "RAPPORT DE FIN DE JOURNÉE", true);
        }, 1000);
      }

    } catch (err) {
      console.error("❌ Erreur fin de journée:", err);
      alert(err.response?.data?.message || "Erreur lors de la clôture");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 TÉLÉCHARGER RAPPORT (CODE ORIGINAL)
  const downloadClosureReport = (closure) => {
    generateDetailedPDF(
      closure, 
      `RAPPORT ${closure.closureType === "passation" ? "PASSATION" : "FIN DE JOURNÉE"}`,
      closure.closureType === "fin_journee"
    );
  };

  // 🔥 ORGANISER LES CLÔTURES PAR DATE (CODE ORIGINAL)
  const getClosuresByDate = () => {
    const grouped = {};
    
    allClosures
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .forEach(closure => {
        const dateKey = new Date(closure.date).toLocaleDateString('fr-FR');
        if (!grouped[dateKey]) {
          grouped[dateKey] = { passation: null, fin_journee: null };
        }
        grouped[dateKey][closure.closureType] = closure;
      });
    
    return grouped;
  };

  const closuresByDate = getClosuresByDate();

  // 🔥 AJOUT: RAFRAÎCHIR LES TABLES EN ATTENTE
  const refreshPendingTables = async () => {
    setLoading(true);
    await checkPendingTables();
    setLoading(false);
  };

  return (
  <div className="daily-closure-container">
    <h2>📊 Clôture Journalière - {userLocationName}</h2>

    {loading && <div className="loading">Chargement en cours...</div>}
    {error && <div className="error-message">{error}</div>}
    {debugInfo && <div className="debug-info">{debugInfo}</div>}

    {closureAlreadyDone && (
      <div className="closure-blocked-warning">
        ⚠️ CLÔTURE DE FIN DE JOURNÉE DÉJÀ EFFECTUÉE - OPÉRATION BLOQUÉE
      </div>
    )}

    <div className="location-info">
      <strong>📍 Localisation : {userLocationName}</strong>
      <br />
      <strong>👤 Utilisateur : {user?.name || "Non connecté"}</strong>
      <br />
      <small>ID Localisation: {userLocationId}</small>
    </div>
    <div className="sales-summary">
        <h3>📈 Ventes aujourd'hui</h3>
        <div className="summary-cards">
          <div className="summary-card">
            <span className="card-value">{sales.length}</span>
            <span className="card-label">Factures</span>
          </div>
          <div className="summary-card">
            <span className="card-value">
              {sales.reduce((acc, bill) => acc + Number(bill.total || 0), 0).toFixed(2)} DT
            </span>
            <span className="card-label">Total</span>
          </div>
        </div>
      </div>

      <div className="closure-buttons">
        <button 
          onClick={handlePassationClosure} 
          disabled={loading || sales.length === 0 || closureAlreadyDone} 
          className="passation-btn"
        >
          🔄 Clôture de Passation
        </button>

        {/* 🔥 MODIFICATION: Ajout de hasPendingTables dans disabled */}
        <button 
          onClick={handleEndOfDayClosure} 
          disabled={loading || sales.length === 0 || !userLocationId || closureAlreadyDone || hasPendingTables} 
          className="endday-btn"
        >
          {closureAlreadyDone ? "🚫 CLÔTURE DÉJÀ FAITE" : 
           hasPendingTables ? `🚫 ${pendingTables.length} TABLES EN ATTENTE` : 
           "🏁 Clôture de Fin de Journée"}
        </button>

        <button
          onClick={() => generateDetailedPDF(passationClosure, "RAPPORT DE PASSATION")}
          disabled={!passationClosure}
          className="report-btn"
        >
          🧾 PDF Passation
        </button>

        <button
          onClick={() => generateDetailedPDF(endOfDayClosure, "RAPPORT DE FIN DE JOURNÉE", true)}
          disabled={!endOfDayClosure}
          className="report-btn"
        >
          🧾 PDF Fin de Journée
        </button>
      </div>

      <div className="closures-history">
        <h3>📋 Historique des Clôtures - {userLocationName}</h3>
        {allClosures.length === 0 ? (
          <p>Aucune clôture trouvée pour cette localisation</p>
        ) : (
          <div className="closures-table">
            <div className="table-header">
              <div>Date</div>
              <div>Passation</div>
              <div>Fin de Journée</div>
              <div>Actions</div>
            </div>
            <div className="table-body">
              {Object.entries(closuresByDate).map(([date, closures]) => (
                <div key={date} className="table-row">
                  <div className="date-cell">{date}</div>
                  
                  <div className="closure-cell">
                    {closures.passation ? (
                      <div className="closure-info">
                        <span className="closure-type">🔄 Passation</span>
                        <span>{closures.passation.totalBills} factures</span>
                        <span>{closures.passation.totalSales?.toFixed(2)} DT</span>
                        <span>{new Date(closures.passation.date).toLocaleTimeString('fr-FR')}</span>
                      </div>
                    ) : (
                      <span className="no-closure">-</span>
                    )}
                  </div>
                  
                  <div className="closure-cell">
                    {closures.fin_journee ? (
                      <div className="closure-info">
                        <span className="closure-type">🏁 Fin de Journée</span>
                        <span>{closures.fin_journee.totalBills} factures</span>
                        <span>{closures.fin_journee.totalSales?.toFixed(2)} DT</span>
                        <span>{new Date(closures.fin_journee.date).toLocaleTimeString('fr-FR')}</span>
                      </div>
                    ) : (
                      <span className="no-closure">-</span>
                    )}
                  </div>
                  
                  <div className="actions-cell">
                    {closures.passation && (
                      <button 
                        onClick={() => downloadClosureReport(closures.passation)}
                        className="download-btn small"
                      >
                        📥 Passation
                      </button>
                    )}
                    {closures.fin_journee && (
                      <button 
                        onClick={() => downloadClosureReport(closures.fin_journee)}
                        className="download-btn small"
                      >
                        📥 Fin Journée
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="closures-status">
        {passationClosure && (
          <div className="closure-card">
            <h3>✅ Clôture de Passation</h3>
            <p>Date: {new Date(passationClosure.date).toLocaleDateString('fr-FR')}</p>
            <p>Heure: {new Date(passationClosure.date).toLocaleTimeString('fr-FR')}</p>
            <p>Total: {passationClosure.totalSales?.toFixed(2)} DT</p>
            <p>Factures: {passationClosure.totalBills}</p>
          </div>
        )}
        
        {endOfDayClosure && (
          <div className="closure-card">
            <h3>✅ Clôture de Fin de Journée</h3>
            <p>Date: {new Date(endOfDayClosure.date).toLocaleDateString('fr-FR')}</p>
            <p>Heure: {new Date(endOfDayClosure.date).toLocaleTimeString('fr-FR')}</p>
            <p>Total: {endOfDayClosure.totalSales?.toFixed(2)} DT</p>
            <p>Factures: {endOfDayClosure.totalBills}</p>
          </div>
        )}
      </div>

    {/* 🔥 SECTION TABLES EN ATTENTE SIMPLIFIÉE */}
    <div className="pending-tables-section">
      <div className="pending-tables-header">
        <h3>📋 État des Tables</h3>
        <div className="pending-tables-stats">
          <span className={`status-indicator ${hasPendingTables ? 'has-pending' : 'no-pending'}`}>
            {hasPendingTables ? '❌' : '✅'}
          </span>
          <span className="count-badge">
            {pendingTables.length} table{pendingTables.length !== 1 ? 's' : ''} en attente
          </span>
          <button 
            onClick={refreshPendingTables} 
            className="refresh-btn small"
            disabled={loading}
            title="Rafraîchir"
          >
            {loading ? '⏳' : '🔄'}
          </button>
        </div>
      </div>
      
      {hasPendingTables ? (
        <div className="pending-tables-summary">
          <div className="warning-box">
            <div className="warning-icon">⚠️</div>
            <div className="warning-content">
              <p className="warning-title">Clôture bloquée</p>
              <p className="warning-message">
                Il y a {pendingTables.length} table{pendingTables.length !== 1 ? 's' : ''} en attente
              </p>
            </div>
          </div>
          
          {/* Affichage compact des tables */}
          <div className="compact-tables-list">
            {pendingTables.slice(0, 3).map((table, index) => (
              <div key={table._id || table.tableNumber} className="compact-table-item">
                <div className="table-number">Table {table.tableNumber}</div>
                <div className="table-details">
                  <span className="item-count">{table.items?.length || 0} art.</span>
                  <span className="table-status">
                    {table.status === "locked" ? "🔒" : "⏳"}
                  </span>
                </div>
              </div>
            ))}
            
            {/* Afficher un indicateur s'il y a plus de 3 tables */}
            {pendingTables.length > 3 && (
              <div className="more-tables">
                + {pendingTables.length - 3} autre{pendingTables.length - 3 !== 1 ? 's' : ''} table{pendingTables.length - 3 !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          
          {/* Bouton pour voir tous les détails (optionnel) */}
          <button 
            className="view-details-btn"
            onClick={() => {
              alert(`Tables en attente détaillées:\n\n${pendingTables.map(t => 
                `• Table ${t.tableNumber}: ${t.items?.length || 0} articles (${t.status === "locked" ? "Verrouillée" : "En cours"})`
              ).join('\n')}`);
            }}
          >
            Voir le détail
          </button>
        </div>
      ) : (
        <div className="no-pending-tables">
          <div className="success-icon">✅</div>
          <p>Aucune table en attente - Clôture autorisée</p>
        </div>
      )}
    </div>

      
    </div>
  );
}

export default DailyClosure;